/* eslint-disable react/jsx-key */
import { Path } from '@vimeo/iris/components';
import { Header, Link, Paragraph } from '@vimeo/iris/typography';

import CodeBlock from 'src/_components/CodeBlock';
import { FancyList, FancyListItem, FancyListTableOfContents } from 'src/_components/FancyList';
import GuideTable from 'src/_components/GuideTable';
import GuideWrapper from 'src/_components/GuideWrapper';
import Meta from 'src/_components/Meta';
import StyledNotice from 'src/_components/StyledNotice';

export default function UploadThumbnails() {
  return (
    <>
      <Meta
        title="Working with Thumbnail Uploads"
        description="Get step-by-step instructions and best practices for adding thumbnail images to videos through the Vimeo API."
      />
      <GuideWrapper
        breadcrumbs={
          <Path>
            <Path.Link href={'/'}>Home</Path.Link>
            <Path.Link href={'/api/guides/start'}>API</Path.Link>
            <Path.Current>Working with Thumbnail Uploads</Path.Current>
          </Path>
        }
        nav={{ page: 'upload-thumbnails' }}
      >
        <Header>Working with Thumbnail Uploads</Header>

        <Paragraph size="2">
          Want a quick, easy way to drive interest to a video? Upload a custom image file to use as
          the video's thumbnail. The <em>thumbnail</em> is the static image that represents the
          video in search results and so on. It's the first impression of the video that the Vimeo
          community receives, so you want to pick an image that helps to tell the video's story.
        </Paragraph>

        <Paragraph size="2">
          If you're in the market to upload a thumbnail image to a video by way of the awesomeness
          of the Vimeo API, you're in exactly the right place. This guide covers everything that you
          need to know to do just that.
        </Paragraph>

        <StyledNotice>
          <strong>NOTE:</strong> This information is current for our latest API release, version
          3.4. For complete details on 3.4, see our{' '}
          <Link variant="minimal" href="/api/changelog">
            Changelog
          </Link>
          .
        </StyledNotice>

        <Header size="2" id="before-you-begin">
          Before you begin
        </Header>
        <Paragraph size="2">To use this guide, you need:</Paragraph>

        <ul>
          <li>
            <strong>A video on Vimeo.</strong> See{' '}
            <Link variant="minimal" href="/api/upload/videos">
              Working with Video Uploads
            </Link>{' '}
            for more information.
          </li>
          <li>
            <strong>An image file.</strong> Use a common web format like JPEG or PNG. If you don't
            already have a thumbnail image, you can create one from a frame of the video. For more
            information, see{' '}
            <Link variant="minimal" href="#creating-a-thumbnail">
              Creating a thumbnail
            </Link>{' '}
            below.
          </li>
          <li>
            <strong>An API application.</strong> This is for making API requests. See{' '}
            <Link variant="minimal" href="/api/guides/start">
              Getting Started: The Basics
            </Link>{' '}
            for details.
          </li>
          <li>
            <strong>
              An access token with the{' '}
              <code>
                <b>upload</b>
              </code>{' '}
              and{' '}
              <code>
                <b>edit</b>
              </code>{' '}
              scopes.
            </strong>{' '}
            This is the password to get past the bouncer. See{' '}
            <Link variant="minimal" href="/api/guides/start">
              Getting Started: The Basics
            </Link>{' '}
            to learn more.
          </li>
        </ul>

        <StyledNotice>
          <strong>NOTE:</strong> You might also consider using our official libraries for PHP,
          Python, Node.js, and a dozen other programming languages. The libraries help to simplify
          complicated coding by handling all the HTTP requests on your behalf. For more information,
          see our{' '}
          <Link variant="minimal" href="/api/libraries">
            Libraries and SDKs
          </Link>{' '}
          page.
        </StyledNotice>

        <Header size="2" id="uploading-a-thumbnail">
          Uploading a thumbnail
        </Header>
        <Paragraph size="2">
          Are you ready to upload a thumbnail? Great! We've got a couple of uploading options that
          you can choose from below, one for videos and one for events, and we'll walk you through
          each process.
        </Paragraph>

        <Header size="3" id="uploading-a-thumbnail-to-a-video">
          Uploading a thumbnail to a video
        </Header>
        <Paragraph size="2">
          For uploading a thumbnail image to a video, the general API workflow is this:
        </Paragraph>

        <FancyListTableOfContents
          anchorPrefix="uploading-a-thumbnail"
          items={[
            'Get the URI of the thumbnail.',
            'Get the upload link for the thumbnail.',
            'Upload the thumbnail image file.',
            'Set the thumbnail as active.',
          ]}
        />

        <Paragraph size="2">We dig into the specifics in the following sections.</Paragraph>
        <FancyList anchorPrefix="uploading-a-thumbnail">
          <FancyListItem>
            <Header size="4">Get the URI of the thumbnail</Header>
            <Paragraph size="2">
              Whenever you upload a video to Vimeo, we set aside a URI to represent the video's
              thumbnails. Find this URI in the{' '}
              <code>
                <b>metadata.connections.pictures.uri</b>
              </code>{' '}
              field of the video representation, which you can retrieve from the API with an
              authenticated GET request:
            </Paragraph>

            <CodeBlock
              endpoint={{
                method: 'get',
                resource: '/videos/{video_id}?fields=metadata.connections.pictures.uri',
              }}
            />

            <Paragraph size="2">
              Refer to{' '}
              <Link variant="minimal" href="#table-1">
                Table 1
              </Link>{' '}
              for the headers.
            </Paragraph>

            <GuideTable
              tableNumber={1}
              description={
                <span>
                  Headers for GET to <code>/videos/{`{video_id}`}</code>
                </span>
              }
              headers={['Header', 'Set value to']}
              rows={[
                [
                  <code>
                    <b>Authorization</b>
                  </code>,
                  <code>bearer {`{access_token}`}</code>,
                ],
                [
                  <code>
                    <b>Accept</b>
                  </code>,
                  <code>application/vnd.vimeo.*+json;version=3.4</code>,
                ],
              ]}
            />

            <StyledNotice>
              <strong>NOTE:</strong> Don't omit the{' '}
              <code>
                <b>Accept</b>
              </code>{' '}
              header in this request (and all subsequent requests in this guide), and always be sure
              to set it to the given value. This ensures that the API handles your response
              according to version 3.4. For more information, see{' '}
              <Link variant="minimal" href="/api/common-formats#using-the-accept-header">
                Using Common Formats and Parameters
              </Link>
              .
            </StyledNotice>

            <Paragraph size="2">
              Look for{' '}
              <code>
                <b>metadata.connections.pictures.uri</b>
              </code>{' '}
              in the response. You'll use this value in the next step.
            </Paragraph>
          </FancyListItem>
          <FancyListItem>
            <Header size="4">Get the upload link for the thumbnail</Header>
            <Paragraph size="2">
              You obtain the location for uploading the thumbnail when you create the thumbnail's{' '}
              <em>resource</em>, or the placeholder for the image file on our servers.
            </Paragraph>

            <Paragraph size="2">
              To create this resource, refer back to the URI from the previous step, and send us the
              following authenticated POST request:
            </Paragraph>

            <CodeBlock endpoint={{ method: 'post', resource: '{pictures_uri}' }} />

            <Paragraph size="2">
              For the headers, refer to{' '}
              <Link variant="minimal" href="#table-1">
                Table 1
              </Link>
              . You want{' '}
              <code>
                <b>Authorization</b>
              </code>{' '}
              as <code>bearer {`{access_token}`}</code> and{' '}
              <code>
                <b>Accept</b>
              </code>{' '}
              as <code>application/vnd.vimeo.*+json;version=3.4</code>.
            </Paragraph>

            <Paragraph size="2">
              The HTTP status of 201 Created indicates that your thumbnail resource is ready.
            </Paragraph>

            <Paragraph size="2">
              In the response from the API, look for the{' '}
              <code>
                <b>link</b>
              </code>{' '}
              field. The value of{' '}
              <code>
                <b>link</b>
              </code>{' '}
              gives the upload URL for{' '}
              <Link variant="minimal" href="#uploading-a-thumbnail-step-3">
                Step 3
              </Link>
              .
            </Paragraph>
          </FancyListItem>
          <FancyListItem>
            <Header size="4">Upload the thumbnail image file</Header>
            <Paragraph size="2">
              All set with the{' '}
              <code>
                <b>link</b>
              </code>{' '}
              field from the previous step? It's time to upload the image file. For best results, we
              recommend that you send us a common web format like JPEG or PNG instead of something
              fancy.
            </Paragraph>

            <Paragraph size="2">
              The upload itself is just a PUT to the upload URL, where the body of the request
              contains the binary data of the image file:
            </Paragraph>

            <CodeBlock endpoint={{ method: 'put', resource: '{link}', httpPrefix: false }} />

            <Paragraph size="2">
              See{' '}
              <Link variant="minimal" href="#table-2">
                Table 2
              </Link>{' '}
              for the proper headers.
            </Paragraph>

            <GuideTable
              tableNumber={2}
              description={
                <span>
                  Headers for PUT to <code>{`{link}`}</code>
                </span>
              }
              headers={['Header', 'Set value to']}
              rows={[
                [
                  <code>
                    <b>Content-Type</b>
                  </code>,
                  <span>
                    <code>image/jpeg</code> or <code>image/png</code>, depending on the image format
                  </span>,
                ],
              ]}
            />
            <Paragraph size="2">
              If you see HTTP status 200 OK, the upload was successful.
            </Paragraph>
          </FancyListItem>
          <FancyListItem>
            <Header size="4">Set the thumbnail as active</Header>
            <Paragraph size="2">
              A video can have multiple thumbnail images, but only one of them can be active at a
              time. So, in this final step, you mark the thumbnail that you just uploaded as the
              active one for the video. To do this, take the thumbnail URI from{' '}
              <Link variant="minimal" href="#uploading-a-thumbnail-step-2">
                Step 2
              </Link>
              {', '}
              and PATCH to that location:
            </Paragraph>

            <CodeBlock endpoint={{ method: 'patch', resource: '{pictures_uri}' }} />

            <Paragraph size="2">
              See{' '}
              <Link variant="minimal" href="#table-3">
                Table 3
              </Link>{' '}
              for the headers.
            </Paragraph>

            <GuideTable
              tableNumber={3}
              description={
                <span>
                  Headers for PATCH to <code>{`{uri}`}</code>
                </span>
              }
              headers={['Header', 'Set value to']}
              rows={[
                [
                  <code>
                    <b>Authorization</b>
                  </code>,
                  <code>bearer {`{access_token}`}</code>,
                ],
                [
                  <code>
                    <b>Content-Type</b>
                  </code>,
                  <code>application/json</code>,
                ],
                [
                  <code>
                    <b>Accept</b>
                  </code>,
                  <code>application/vnd.vimeo.*+json;version=3.4</code>,
                ],
              ]}
            />

            <Paragraph size="2">
              In the body of the request, set the{' '}
              <code>
                <b>active</b>
              </code>{' '}
              parameter to <code>true</code>:
            </Paragraph>

            <CodeBlock language="json">{{ active: true }}</CodeBlock>

            <Paragraph size="2">
              Look for HTTP status 200 OK. If you see it, all is well with the world, or at least
              with the active status of your thumbnail.
            </Paragraph>
          </FancyListItem>
        </FancyList>

        <Header size="3" id="uploading-a-thumbnail-to-a-live-event">
          Uploading a thumbnail to an event
        </Header>
        <Paragraph size="2">
          If you're looking to upload a thumbnail image to an event, these are the steps to follow:
        </Paragraph>

        <FancyListTableOfContents
          anchorPrefix="uploading-a-thumbnail-to-a-live-event"
          items={[
            'Create the event and get the upload link.',
            'Upload an example thumbnail.',
            'Activate the thumbnail.',
          ]}
        />

        <Paragraph size="2">We dive deeper into the process below.</Paragraph>

        <FancyList anchorPrefix="uploading-a-thumbnail-to-a-live-event">
          <FancyListItem>
            <Header size="4">Create the event and get the upload link</Header>
            <Paragraph size="2">
              Before you can upload a thumbnail to an event, you have to create the event. You
              create the event by sending a POST request to the following endpoint:
            </Paragraph>

            <CodeBlock
              endpoint={{
                method: 'post',
                resource: '/me/live_events/{event_id}/pictures?fields=link,uri',
              }}
            />
            <Paragraph size="2">
              The API response gives you the value of the{' '}
              <code>
                <b>link</b>
              </code>{' '}
              field, which is the upload link for{' '}
              <Link variant="minimal" href="#uploading-a-thumbnail-to-a-live-event-step-2">
                Step 2
              </Link>
              {', '}and the{' '}
              <code>
                <b>uri</b>
              </code>{' '}
              field, which is the link for{' '}
              <code>
                <b>pictures_uri</b>
              </code>{' '}
              in{' '}
              <Link variant="minimal" href="#uploading-a-thumbnail-to-a-live-event-step-3">
                Step 3
              </Link>{' '}
              below.
            </Paragraph>
            <Paragraph size="2">
              Header information is in{' '}
              <Link variant="minimal" href="#table-1">
                Table 1
              </Link>{' '}
              above. You want to send{' '}
              <code>
                <b>Authorization</b>
              </code>{' '}
              as <code>{`bearer {access_token}`}</code>.
            </Paragraph>

            <Paragraph size="2">
              When you see the HTTP status of 201 Created, you're ready for{' '}
              <Link variant="minimal" href="#uploading-a-thumbnail-to-a-live-event-step-2">
                Step 2
              </Link>
              .
            </Paragraph>
          </FancyListItem>
          <FancyListItem>
            <Header size="4">Upload a thumbnail image</Header>
            <Paragraph size="2">
              Remember the link that you generated in{' '}
              <Link variant="minimal" href="#uploading-a-thumbnail-to-a-live-event-step-1">
                Step 1
              </Link>
              ? Use that here to upload the thumbnail image that you want to use in either of these
              formats: <code>image/jpeg</code> or <code>image/png</code>.
            </Paragraph>

            <Paragraph size="2">
              To upload the thumbnail image, send a PUT to the upload URL:
            </Paragraph>

            <CodeBlock endpoint={{ method: 'put', resource: '{link}' }} />

            <Paragraph size="2">
              See{' '}
              <Link variant="minimal" href="#table-4">
                Table 4
              </Link>{' '}
              for the headers.
            </Paragraph>

            <GuideTable
              tableNumber={4}
              description={
                <span>
                  Headers for PUT <code>{`{link}`}</code>
                </span>
              }
              headers={['Header', 'Set value to']}
              rows={[
                [
                  <code>
                    <b>Content-Type</b>
                  </code>,
                  <>
                    <code>image/jpeg</code> or <code>image/png</code>, depending on the image format
                  </>,
                ],
                [
                  <code>
                    <b>Accept</b>
                  </code>,
                  <code>application/vnd.vimeo.*+json;version=3.4</code>,
                ],
              ]}
            />
            <StyledNotice>
              <strong>NOTE:</strong> The{' '}
              <code>
                <b>Authorization</b>
              </code>{' '}
              header isn't needed in this step.
            </StyledNotice>
            <Paragraph size="2">
              In the body of the request, include the binary thumbnail data.
            </Paragraph>
          </FancyListItem>
          <FancyListItem>
            <Header size="4">Activate the thumbnail</Header>
            <Paragraph size="2">
              Since you can have only one active thumbnail image at a time, activate the thumbnail
              image that you just uploaded for the event. You do this by taking the thumbnail URI
              from{' '}
              <Link variant="minimal" href="#uploading-a-thumbnail-to-a-live-event-step-1">
                Step 1
              </Link>{' '}
              and PATCHing to that location:
            </Paragraph>

            <CodeBlock endpoint={{ method: 'PATCH', resource: '{pictures_uri}' }} />

            <Paragraph size="2">
              See{' '}
              <Link variant="minimal" href="#table-3">
                Table 3
              </Link>{' '}
              above for the headers.
            </Paragraph>
            <Paragraph size="2">
              In the body of the request, set the{' '}
              <code>
                <b>active</b>
              </code>{' '}
              parameter to <code>true</code>:
            </Paragraph>

            <CodeBlock language="json">{{ active: true }}</CodeBlock>
            <Paragraph size="2">
              A returned HTTP status of 200 OK means that the active status of your thumbnail is
              good, as in awesome, just like you.
            </Paragraph>
          </FancyListItem>
        </FancyList>
        <Header size="2" id="creating-a-thumbnail">
          Creating a thumbnail
        </Header>
        <Paragraph size="2">
          You can upload your own image file to serve as the thumbnail if you want, but you can also
          create the thumbnail from a frame of the video.
        </Paragraph>

        <Paragraph size="2">
          To create the thumbnail, make sure that your app has an access token with the{' '}
          <code>upload</code> scope, and send the following authenticated POST request:
        </Paragraph>

        <CodeBlock endpoint={{ method: 'post', resource: '/videos/{video_id}/pictures' }} />

        <Paragraph size="2">
          For the headers, use the ones from{' '}
          <Link variant="minimal" href="#table-3">
            Table 3
          </Link>
          . Set{' '}
          <code>
            <b>Authorization</b>
          </code>{' '}
          to <code>{`bearer {access_token}`}</code>,{' '}
          <code>
            <b>Content-Type</b>
          </code>{' '}
          to <code>application/json</code>, and{' '}
          <code>
            <b>Accept</b>
          </code>{' '}
          to <code>application/vnd.vimeo.*+json;version=3.4</code>.
        </Paragraph>

        <Paragraph size="2">
          In the body of the request, set the{' '}
          <code>
            <b>time</b>
          </code>{' '}
          parameter to the time in seconds where we should grab the frame. While you're at it, set{' '}
          <code>
            <b>active</b>
          </code>{' '}
          to <code>true</code>, to turn on the thumbnail for active use:
        </Paragraph>

        <CodeBlock language="json">
          {{
            time: '{time}',
            active: true,
          }}
        </CodeBlock>

        <Paragraph size="2">
          The API returns HTTP status 201, meaning that the thumbnail is created.
        </Paragraph>

        <Header size="2" id="change-active-status-of-a-thumbnail">
          Changing the active status of a thumbnail
        </Header>
        <Paragraph size="2">
          A video can have multiple thumbnails, but only one of them can be active, or visible, at a
          time. The thumbnail's{' '}
          <code>
            <b>active</b>
          </code>{' '}
          parameter controls this status, where <code>true</code> stands for active and{' '}
          <code>false</code> for inactive.
        </Paragraph>

        <Paragraph size="2">
          To change the active status of a thumbnail image through the API, use the following
          procedure:
        </Paragraph>

        <FancyListTableOfContents
          anchorPrefix="change-active-status-of-a-thumbnail"
          items={['Get the URI of the thumbnail.', 'Set the new active status.']}
        />

        <Paragraph size="2">Want some specifics? Keep reading.</Paragraph>

        <FancyList anchorPrefix="change-active-status-of-a-thumbnail">
          <FancyListItem>
            <Header size="3">Get the URI of the thumbnail</Header>
            <Paragraph size="2">
              To start, send an authenticated GET request to{' '}
              <code>/videos/{`{video_id}`}/pictures</code>:
            </Paragraph>

            <CodeBlock endpoint={{ method: 'get', resource: '/videos/{video_id}/pictures' }} />

            <Paragraph size="2">
              Include the same headers from{' '}
              <Link variant="minimal" href="#table-1">
                Table 1
              </Link>
              . You want{' '}
              <code>
                <b>Authorization</b>
              </code>{' '}
              as <code>{`bearer {access_token}`}</code> and{' '}
              <code>
                <b>Accept</b>
              </code>{' '}
              as <code>application/vnd.vimeo.*+json;version=3.4</code>.
            </Paragraph>

            <Paragraph size="2">
              The API returns a representation of all the thumbnails associated with the video. Find
              the{' '}
              <code>
                <b>data.uri</b>
              </code>{' '}
              field for the thumbnail that you want, and make a note of this value. Keep in mind
              that, when you have multiple thumbnails, each thumbnail image has its own{' '}
              <code>
                <b>data.uri</b>
              </code>{' '}
              field, so make sure that you get the right one.
            </Paragraph>
          </FancyListItem>
          <FancyListItem>
            <Header size="3">Set the new active status</Header>
            <Paragraph size="2">
              Using the URI from{' '}
              <Link variant="minimal" href="#change-active-status-of-a-thumbnail-step-1">
                Step 1
              </Link>
              , and with an authorized request, PATCH us the new active status for the thumbnail:
            </Paragraph>

            <CodeBlock endpoint={{ method: 'patch', resource: '{pictures_uri}' }} />

            <Paragraph size="2">
              Take the headers from{' '}
              <Link variant="minimal" href="#table-3">
                Table 3
              </Link>
              :{' '}
              <code>
                <b>Authorization</b>
              </code>{' '}
              as <code>{`bearer {access_token}`}</code>,{' '}
              <code>
                <b>Content-Type</b>
              </code>{' '}
              as <code>application/json</code>, and{' '}
              <code>
                <b>Accept</b>
              </code>{' '}
              as <code>application/vnd.vimeo.*+json;version=3.4</code>.
            </Paragraph>

            <Paragraph size="2">
              The{' '}
              <code>
                <b>active</b>
              </code>{' '}
              parameter goes in the body of the request:
            </Paragraph>

            <CodeBlock language="json">{{ active: false }}</CodeBlock>

            <Paragraph size="2">
              Setting{' '}
              <code>
                <b>active</b>
              </code>{' '}
              to <code>false</code> deactivates the thumbnail.
            </Paragraph>

            <Paragraph size="2">
              The HTTP status 200 OK means that you've had spectacular success.
            </Paragraph>
          </FancyListItem>
        </FancyList>

        <Header size="2" id="returning-thumbnail-links">
          Returning thumbnail links
        </Header>
        <Paragraph size="2">
          You can return links to thumbnail images using a couple different approaches based on the
          dimensions of the thumbnails that you need. One approach is to return the links to all the
          various sizes of thumbnails that are associated with a video. The other is to return only
          those thumbnails that match the precise dimensions that you give.
        </Paragraph>

        <Paragraph size="2">
          Do we cover both approaches in the following sections? Of course we do.
        </Paragraph>

        <Header size="3" id="returning-links-to-all-thumbnails">
          Returning links to all thumbnails
        </Header>
        <Paragraph size="2">
          To return all thumbnail image links, send an authenticated GET request to{' '}
          <code>/videos/{`{video_id}`}/pictures</code>:
        </Paragraph>

        <CodeBlock endpoint={{ method: 'get', resource: '/videos/{video_id}/pictures' }} />

        <StyledNotice>
          <strong>NOTE:</strong> If you're concerned about hitting request limits, try{' '}
          <code>GET https://api.vimeo.com/videos/{'{video_id}'}?fields=pictures</code> instead. This
          technique is called <i>field filtering.</i> To learn more, see our{' '}
          <Link variant="minimal" href="/guidelines/rate-limiting#avoid-rate-limiting">
            Working with Rate Limits
          </Link>{' '}
          guide.
        </StyledNotice>

        <Paragraph size="2">
          Be prepared to look through the results to find the link to the thumbnail that you need.
          If you happen to know the thumbnail's ID, you can save yourself the scavenger hunt by
          including the ID in the endpoint, like this:
        </Paragraph>

        <CodeBlock
          endpoint={{ method: 'get', resource: '/videos/{video_id}/pictures/{picture_id}' }}
        />

        <Paragraph size="2">
          Use the same headers from{' '}
          <Link variant="minimal" href="#table-1">
            Table 1
          </Link>
          :{' '}
          <code>
            <b>Authorization</b>
          </code>{' '}
          as <code>{`bearer {access_token}`}</code> and{' '}
          <code>
            <b>Accept</b>
          </code>{' '}
          as <code>application/vnd.vimeo.*+json;version=3.4</code>.
        </Paragraph>

        <Paragraph size="2">
          The API returns multiple thumbnail links, and of different kinds. The base thumbnail link
          — that is, the no-frills link to the image file — appears in the{' '}
          <code>
            <b>base_link</b>
          </code>{' '}
          field. We also offer a set of standard-sized thumbnails at automatically generated preset
          dimensions. These appear in the response's{' '}
          <code>
            <b>sizes.link</b>
          </code>{' '}
          fields, along with separate versions of each size thumbnail with a built-in play button
          overlay in the{' '}
          <code>
            <b>sizes.link_with_play_button</b>
          </code>{' '}
          fields:
        </Paragraph>

        <CodeBlock language="json">
          {{
            uri: '/videos/286898202/pictures/123456789',
            active: true,
            base_link:
              'https://i.vimeocdn.com/video/123456789-ba7816bf8f01cfea414140de5dae2223b00361a396177a',
            type: 'custom',
            sizes: [
              {
                width: 100,
                height: 75,
                link: 'https://i.vimeocdn.com/video/123456789-ba7816bf8f01cfea414140de5dae2223b00361a396177a_100x75',
                link_with_play_button:
                  'https://i.vimeocdn.com/filter/overlay?src0=https%3A%2F%2Fi.vimeocdn.com%2Fvideo%2F123456789-ba7816bf8f01cfea414140de5dae2223b00361a396177a_100x75&src1=http%3A%2F%2Ff.vimeocdn.com%2Fp%2Fimages%2Fcrawler_play.png',
              },
              {
                width: 1920,
                height: 1080,
                link: 'https://i.vimeocdn.com/video/123456789-ba7816bf8f01cfea414140de5dae2223b00361a396177a_1920x1080',
                link_with_play_button:
                  'https://i.vimeocdn.com/filter/overlay?src0=https%3A%2F%2Fi.vimeocdn.com%2Fvideo%2F123456789-ba7816bf8f01cfea414140de5dae2223b00361a396177a_1920x1080&src1=http%3A%2F%2Ff.vimeocdn.com%2Fp%2Fimages%2Fcrawler_play.png',
              },
            ],
            resource_key: '8bbf3f07e660574acf7dadb60c46a92fc907b73d',
            default_picture: false,
          }}
        </CodeBlock>

        <Header size="3" id="returning-thumbnails-of-a-specific-size">
          Returning the thumbnails of a specific size only
        </Header>
        <Paragraph size="2">
          To return the thumbnails of a specific size — one of our standard presets, your own custom
          dimensions — send an authenticated GET request to the{' '}
          <code>/videos/{'{video_id}'}/pictures</code> endpoint but with the{' '}
          <code>
            <b>sizes</b>
          </code>{' '}
          parameter appended, like this:
        </Paragraph>

        <CodeBlock
          endpoint={{
            method: 'get',
            resource: '/videos/{video_id}/pictures?sizes={width}x{height}',
          }}
        />

        <Paragraph size="2">
          Supply the desired width and height in the <code>{'{width}'}</code> and{' '}
          <code>{'{height}'}</code> placeholders; the units are pixels. As for the headers, see{' '}
          <Link variant="minimal" href="#table-1">
            Table 1
          </Link>
          . That's{' '}
          <code>
            <b>Authorization</b>
          </code>{' '}
          as <code>bearer {`{access_token}`}</code> and{' '}
          <code>
            <b>Accept</b>
          </code>{' '}
          as <code>application/vnd.vimeo.*+json;version=3.4</code>.
        </Paragraph>

        <StyledNotice>
          <strong>NOTE:</strong> For more information on the{' '}
          <code>
            <b>sizes</b>
          </code>{' '}
          parameter, see{' '}
          <Link variant="minimal" href="/api/common-formats#using-the-sizes-parameter">
            Using Common Formats and Parameters
          </Link>
          .
        </StyledNotice>

        <Paragraph size="2">
          Example time. You want only the 150 ✕ 100 thumbnail for the video with the ID of
          286898202. You send your authenticated GET request to{' '}
          <code>https://api.vimeo.com/videos/286898202/pictures?sizes=150x100.</code> The API gives
          you back this response:
        </Paragraph>

        <CodeBlock language="json">
          {{
            uri: '/videos/286898202/pictures/123456789',
            active: true,
            base_link:
              'https://i.vimeocdn.com/video/123456789-ba7816bf8f01cfea414140de5dae2223b00361a396177a',
            type: 'custom',
            sizes: [
              {
                width: 150,
                height: 100,
                link: 'https://i.vimeocdn.com/video/123456789-ba7816bf8f01cfea414140de5dae2223b00361a396177a_150x100?r=pad',
                link_with_play_button:
                  'https://i.vimeocdn.com/filter/overlay?src0=https%3A%2F%2Fi.vimeocdn.com%2Fvideo%2F123456789-ba7816bf8f01cfea414140de5dae2223b00361a396177a_150x100&src1=http%3A%2F%2Ff.vimeocdn.com%2Fp%2Fimages%2Fcrawler_play.png',
              },
            ],
            resource_key: '8bbf3f07e660574acf7dadb60c46a92fc907b73d',
            default_picture: false,
          }}
        </CodeBlock>

        <Header size="2" id="caching-thumbnails">
          Caching thumbnails
        </Header>
        <Paragraph size="2">
          The links to the thumbnails are valid for one hour from the API response. If you need to
          access these images past that time, say because they're embedded on your website, try
          either of these alternatives:
        </Paragraph>
        <ul>
          <li>
            <Link variant="minimal" href="#download-image-file">
              <strong>Option 1.</strong> Download the image file.
            </Link>
          </li>
          <li>
            <Link variant="minimal" href="#refresh-cached-thumbnail-links-every-hour">
              <strong>Option 2.</strong> Refresh cached thumbnail links every hour.
            </Link>
          </li>
        </ul>

        <Paragraph size="2">For more details, see the following sections.</Paragraph>

        <StyledNotice>
          <strong>NOTE:</strong> You might not need to worry about caching thumbnails if you only
          use the Vimeo Player. Our player automatically displays the most up-to-date thumbnail
          image.
        </StyledNotice>

        <Header size="3" id="download-image-file">
          Option 1. Download the image file
        </Header>
        <Paragraph size="2">
          You can download the thumbnail for storage on your own CMS or hosting provider. Use a cURL
          command to retrieve the image, like this:
        </Paragraph>

        <CodeBlock>{`curl https://i.vimeocdn.com/video/123456789-ba7816bf8f01cfea414140de5dae2223b00361a396177a_100x75.jpg --output 123456789.jpg`}</CodeBlock>

        <Paragraph size="2">
          Note the{' '}
          <b>
            <code>output</code>
          </b>{' '}
          argument, which indicates the file name of the image.
        </Paragraph>

        <Header size="3" id="refresh-cached-thumbnail-links-every-hour">
          Option 2. Refresh the cached thumbnail links every hour
        </Header>
        <Paragraph size="2">
          If you'd prefer Vimeo's hosted images, you can refresh the cached thumbnail links every
          hour, before they expire. These URLs are subject to change, so you should refresh them
          regularly wherever you use them to avoid the risk of your links breaking.
        </Paragraph>
        <Paragraph size="2">
          To refresh the cache, follow the same procedure from{' '}
          <Link variant="minimal" href="#returning-thumbnail-links">
            Returning thumbnail links
          </Link>{' '}
          above. And for best practices on caching, refer to our{' '}
          <Link variant="minimal" href="/guidelines#best-practices">
            Guidelines for Developers
          </Link>
          .
        </Paragraph>

        <Header size="2" id="handling-errors">
          Handling errors
        </Header>
        <Paragraph size="2">
          Since nothing ever goes wrong and nothing unexpected ever happens, we're not sure why
          we're discussing these common upload error cases, but whatever.
        </Paragraph>

        <Paragraph size="2">
          Even more useless information about less frequent errors appears in our{' '}
          <Link variant="minimal" href="/api/reference/videos#upload_video">
            API Reference
          </Link>
          .
        </Paragraph>

        <Header size="3" id="http-status-code-4xx">
          HTTP status code 4XX
        </Header>
        <Paragraph size="2">
          An HTTP status code in the 400s, such as 410 Gone, probably means that you're trying to
          upload to a bad or expired link. To generate a new link, go back to{' '}
          <Link variant="minimal" href="#uploading-a-thumbnail-step-1">
            Step 1
          </Link>{' '}
          of the thumbnail upload process for a video or{' '}
          <Link variant="minimal" href="#uploading-a-thumbnail-to-a-live-event-step-1">
            Step 1
          </Link>{' '}
          of the thumbnail upload process for an event, whichever brought you here, and run the
          upload again.
        </Paragraph>

        <Paragraph size="2">
          If you get the 400 Bad Request status code after the POST or PATCH request in either
          process, you likely have a bad parameter in either the header or the body. Make sure that
          the parameter name is accurate and that you're sending the right data type.
        </Paragraph>

        <Header size="3" id="http-status-code-5xx">
          HTTP status code 5XX
        </Header>
        <Paragraph size="2">
          If the API gives you an HTTP status code in the 500s, such as 500 Internal Server Error,
          we're experiencing some sort of problem with our server. Please wait a couple minutes and
          try again. If the same status code keeps coming back,{' '}
          <Link variant="minimal" href="http://www.vimeo.com/help/contact">
            tell us about it
          </Link>
          .
        </Paragraph>

        <Header size="3" id="error-code-8003-unauthorized-access-token">
          Error code 8003: unrecognized access token
        </Header>
        <Paragraph size="2">
          If you receive error code 8003 and HTTP status 401 Authorization Required, check the
          access token string that you're supplying, because the API doesn't recognize it:
        </Paragraph>

        <CodeBlock
          language="json"
          header={['HTTP/1.1 401 Authorization Required', 'Content-Type: application/json']}
        >
          {{
            error: "Something strange occurred. Please get in touch with the app's creator.",
            link: null,
            developer_message: "The app didn't receive the user's credentials.",
            error_code: 8003,
          }}
        </CodeBlock>

        <Paragraph size="2">
          If the string is fine but you get this error anyway, see our{' '}
          <Link variant="minimal" href="/api/authentication">
            authentication documentation
          </Link>{' '}
          for additional help.
        </Paragraph>

        <Header size="2" id="whats-next">
          What's next?
        </Header>
        <Paragraph size="2">
          Using a very similar process, you can upload text tracks to your videos for subtitles,
          captions, and the like. For more details, see{' '}
          <Link variant="minimal" href="/api/upload/texttracks">
            Working with Text Track Uploads
          </Link>
          . Or if you want to learn more about the technical properties of thumbnail images, along
          with everything else in the Vimeo universe, see our{' '}
          <Link variant="minimal" href="/api/reference">
            API Reference
          </Link>
          .
        </Paragraph>

        <Paragraph size="2">
          If we've missed anything (or totally lost you),{' '}
          <Link variant="minimal" href="http://www.vimeo.com/help/contact">
            contact us
          </Link>{' '}
          for some clarity.
        </Paragraph>
      </GuideWrapper>
    </>
  );
}
